import { Alert, Button, Form, Input, Modal } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { LibraryContext } from './context-provider';

export default function RenameModal({ open, setOpen }) {
   const [form] = Form.useForm();
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState(null);
   const [folderName, setFolderName] = useState('');
   const [validationError, setValidationError] = useState('');
   const { renameFile, selectedLibrary, library } = useContext(LibraryContext);

   useEffect(() => {
      if (open && selectedLibrary?.[0]) {
         form.resetFields();
         setFolderName(selectedLibrary[0].name || '');
         setValidationError('');
         setError(null);
      }
   }, [open, selectedLibrary]);

   // Check for duplicate folder names when renaming folders
   useEffect(() => {
      if (!selectedLibrary?.[0] || selectedLibrary[0].type !== 'folder') return;

      if (!folderName.trim()) {
         setValidationError('');
         return;
      }

      const selectedFolder = selectedLibrary[0];
      const trimmedName = folderName.trim();

      // Don't show error if the name is the same as the current folder name
      if (trimmedName.toLowerCase() === selectedFolder.name.toLowerCase()) {
         setValidationError('');
         return;
      }

      if (library.data && library.data.folders) {
         const existingFolder = library.data.folders.find(
            (folder) => folder.name.toLowerCase() === trimmedName.toLowerCase() && folder._id !== selectedFolder._id
         );

         if (existingFolder) {
            setValidationError('A folder with this name already exists in this location');
         } else {
            setValidationError('');
         }
      }
   }, [folderName, library.data, selectedLibrary]);

   async function handleSubmit() {
      if (validationError) return;

      setLoading(true);
      const values = await form.validateFields();
      const selected = selectedLibrary[0];

      try {
         if (selected.type === 'folder') {
            await library.updateFolder(selected._id, { name: values.name });
         }
         if (selected.type === 'file') {
            await renameFile(selected._id, values.name, selected.extension);
         }
         setError(null);
         setValidationError('');
         setFolderName('');
         setOpen(false);
      } catch (error) {
         setError(error.message);
      }
      setLoading(false);
   }

   if (!selectedLibrary) return null;

   return (
      <>
         <Modal
            title={selectedLibrary[0]?.type === 'folder' ? 'Rename Folder' : 'Rename File'}
            open={open}
            destroyOnClose
            onCancel={() => {
               setOpen(false);
               setFolderName('');
               setValidationError('');
               setError(null);
            }}
            footer={[
               <Button
                  key="back"
                  onClick={() => {
                     setOpen(false);
                     setFolderName('');
                     setValidationError('');
                     setError(null);
                  }}
               >
                  Cancel
               </Button>,
               <Button
                  key="submit"
                  type="primary"
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={!!validationError || (selectedLibrary?.[0]?.type === 'folder' && !folderName.trim())}
               >
                  Rename
               </Button>,
            ]}
         >
            <Form layout="vertical" form={form} onFinish={handleSubmit}>
               <Form.Item
                  name="name"
                  rules={[{ required: true, message: 'Please input file name!' }]}
                  initialValue={selectedLibrary[0]?.name}
                  validateStatus={validationError ? 'error' : ''}
                  help={validationError}
               >
                  <Input
                     placeholder={selectedLibrary[0]?.type === 'folder' ? 'Folder Name' : 'File Name'}
                     value={folderName}
                     onChange={(e) => setFolderName(e.target.value)}
                  />
               </Form.Item>
               {error && <Alert message={error} type="error" showIcon />}
            </Form>
         </Modal>
      </>
   );
}
